// قفل الشاشات المالية (0098) — قرار المالك: «الإدارة المالية والخزنة عاوز لما أجي
// أفتحهم يطلب مني password علشان أقدر أدخل عليهم».
//
// هنا شيئان: **البوّابة** التي تسأل الكلمة قبل عرض الشاشة، و**الإعداد** الذي يضع
// الكلمة ويغيّرها ويرفعها — وهو في «إعدادات الحساب» بقرار المالك.
//
// **والقفل في الخادم**: هذه الشاشة تسأل لأن الخادم سيرفض بلا فتح (`SCREEN_LOCKED`)،
// لا لأنها هي الحارس. ولو فُتحت الواجهة بحيلةٍ ما، ما زال المال محميًّا.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError, get, post, put } from '../lib/api.ts';
import { Alert, Card, Field, Spinner, TextInput } from '../components/ui.tsx';

const msg = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);

export interface LockState { enabled: boolean; unlockedUntil: string | null; minutes: number }

export const readLock = () => get<LockState>('/me/screen-lock');

/**
 * **البوّابة**: لا تعرض ما تحرسه إلا بعد الكلمة.
 *
 * ومن لم يضع قفلًا لا يُسأل شيئًا — فالميزة تُفعَّل بيده لا تُفرض عليه.
 */
export function ScreenLockGate({ title, children }: { title: string; children: ReactNode }) {
  const [state, setState] = useState<LockState | null>(null);
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setState(await readLock()); }
    catch { setState({ enabled: false, unlockedUntil: null, minutes: 15 }); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function unlock() {
    setErr(null); setBusy(true);
    try {
      const r = await post<{ unlockedUntil: string }>('/me/screen-lock/unlock', { lockPassword: pw });
      setPw('');
      setState(s => (s ? { ...s, unlockedUntil: r.unlockedUntil } : s));
    } catch (e) { setErr(msg(e, 'تعذّر الفتح')); }
    finally { setBusy(false); }
  }

  if (state === null) return <Spinner />;
  const open = !state.enabled
    || (state.unlockedUntil !== null && new Date(state.unlockedUntil).getTime() > Date.now());
  if (open) return <>{children}</>;

  return (
    <div className="mx-auto max-w-md">
      <Card title={`🔒 ${title}`}>
        <p className="mb-3 text-sm leading-6 text-ink-600">
          الشاشة دي مقفولة بكلمة تانية غير كلمة الدخول. اكتبها عشان تفتحها — وهتفضل
          مفتوحة {state.minutes} دقيقة وبعدين تتقفل لوحدها.
        </p>
        <form onSubmit={e => { e.preventDefault(); void unlock(); }}>
          <Field label="كلمة القفل" required>
            {/* نوع الحقل password: لا يظهر المكتوب ولا يُحفظ في نموذجٍ يُقرأ */}
            <TextInput type="password" autoComplete="off" value={pw} disabled={busy}
              onChange={e => setPw(e.target.value)} />
          </Field>
          <button type="submit" className="btn-primary mt-3 w-full px-4 py-2"
            disabled={busy || pw.length === 0}>
            {busy ? 'جارٍ الفتح…' : 'افتح'}
          </button>
        </form>
        {err && <div className="mt-3"><Alert>{err}</Alert></div>}
        <p className="mt-3 text-xs text-ink-500">
          نسيتها؟ غيّرها من «إعدادات الحساب ← كلمة مرور الخزنة» بكلمة مرور دخولك.
        </p>
      </Card>
    </div>
  );
}

/**
 * **الإعداد**: وضع الكلمة وتغييرها ورفعها — وكلّها تطلب كلمة **الدخول**.
 *
 * ولولا ذلك لكان من جلس على جهازٍ مفتوح يبدّل القفل بكلمةٍ يعرفها هو، فيصير الباب
 * مفتوحًا لمن يقف أمامه — وهو عين ما وُضع القفل من أجله.
 */
export function ScreenLockSettings() {
  const [state, setState] = useState<LockState | null>(null);
  const [f, setF] = useState({ current: '', lock: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setState(await readLock()); }
    catch (e) { setErr(msg(e, 'تعذّر قراءة حالة القفل')); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const set = (k: keyof typeof f, v: string) => { setF(p => ({ ...p, [k]: v })); setDone(null); };

  async function save() {
    setErr(null); setDone(null);
    if (f.lock !== f.confirm) { setErr('الكلمتين مش زي بعض'); return; }
    setBusy(true);
    try {
      await put('/me/screen-lock', { currentPassword: f.current, lockPassword: f.lock });
      setF({ current: '', lock: '', confirm: '' });
      setDone('اتحفظت. الشاشات المالية هتطلب الكلمة دي.');
      await load();
    } catch (e) { setErr(msg(e, 'تعذّر الحفظ')); }
    finally { setBusy(false); }
  }

  async function remove() {
    setErr(null); setDone(null);
    if (!f.current) { setErr('اكتب كلمة مرور الدخول الأول'); return; }
    if (!confirm('ترفع القفل؟ الشاشات المالية هتتفتح من غير ما تطلب كلمة.')) return;
    setBusy(true);
    try {
      await post('/me/screen-lock/remove', { currentPassword: f.current });
      setF({ current: '', lock: '', confirm: '' });
      setDone('اترفع القفل.');
      await load();
    } catch (e) { setErr(msg(e, 'تعذّر رفع القفل')); }
    finally { setBusy(false); }
  }

  async function lockNow() {
    setBusy(true);
    try { await post('/me/screen-lock/lock', {}); setDone('اتقفلت دلوقتي.'); await load(); }
    catch (e) { setErr(msg(e, 'تعذّر القفل')); }
    finally { setBusy(false); }
  }

  if (state === null) return <Spinner />;
  const openNow = state.unlockedUntil !== null && new Date(state.unlockedUntil).getTime() > Date.now();

  return (
    <div className="space-y-4">
      <Card title="كلمة مرور الخزنة">
        <p className="mb-3 text-sm leading-6 text-ink-600">
          دي كلمة تانية <b>غير كلمة الدخول</b>، بتتطلب لما تفتح <b>«الإدارة المالية»</b> أو
          <b> «الخزنة»</b>. فايدتها إن الجهاز لو فضل مفتوح على مكتبك، اللي يمرّ عليه ميقدرش
          يشوف مرتّبات الناس ولا فلوسك.
        </p>
        <p className="mb-3 text-sm">
          الحالة دلوقتي:{' '}
          {!state.enabled
            ? <b className="text-amber-700">مفيش قفل</b>
            : openNow
              ? <b className="text-emerald-700">مقفولة — ومفتوحة دلوقتي مؤقتًا</b>
              : <b className="text-emerald-700">مقفولة</b>}
        </p>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="كلمة مرور الدخول" required hint="عشان نتأكد إنك إنت">
            <TextInput type="password" autoComplete="off" value={f.current} disabled={busy}
              onChange={e => set('current', e.target.value)} />
          </Field>
          <Field label={state.enabled ? 'كلمة القفل الجديدة' : 'كلمة القفل'} required hint="٦ حروف على الأقل">
            <TextInput type="password" autoComplete="off" value={f.lock} disabled={busy}
              onChange={e => set('lock', e.target.value)} />
          </Field>
          <Field label="تأكيد كلمة القفل" required>
            <TextInput type="password" autoComplete="off" value={f.confirm} disabled={busy}
              onChange={e => set('confirm', e.target.value)} />
          </Field>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button type="button" className="btn-primary px-4 py-2 text-sm"
            disabled={busy || f.current.length === 0 || f.lock.length < 6}
            onClick={() => void save()}>
            {busy ? 'جارٍ الحفظ…' : state.enabled ? 'غيّر كلمة القفل' : 'فعّل القفل'}
          </button>
          {state.enabled && (
            <>
              <button type="button" className="btn-ghost px-4 py-2 text-sm" disabled={busy}
                onClick={() => void remove()}>ارفع القفل</button>
              {openNow && (
                <button type="button" className="btn-ghost px-4 py-2 text-sm" disabled={busy}
                  onClick={() => void lockNow()}>اقفل دلوقتي</button>
              )}
            </>
          )}
          {done && <span className="text-sm text-emerald-700">{done}</span>}
        </div>
        {err && <div className="mt-3"><Alert>{err}</Alert></div>}

        <p className="mt-3 text-xs leading-6 text-ink-500">
          الكلمة متسجّلة عندنا <b>مشفّرة</b> زي كلمة الدخول — محدش يقدر يقراها، ولا إحنا.
          ولو نسيتها غيّرها من هنا بكلمة مرور دخولك.
        </p>
      </Card>
    </div>
  );
}
